import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowRight, Sparkles, RotateCcw } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { StepProgress } from "@/components/fionova/StepProgress";
import { EllieBridge } from "@/components/journey/EllieBridge";
import { Eyebrow, PrimaryButton } from "@/components/fionova/Primitives";
import { futureChat } from "@/services/unsaidService";
import { unsaidStore, transcriptHash } from "@/lib/unsaidStore";
import { onboardingState } from "@/lib/onboardingState";
import { trackSessionOnce } from "@/lib/analytics";

const SUGGESTIONS = [
  "What did I finally stop worrying about?",
  "What mattered most?",
  "What do you wish I'd do right now?",
  "What should I stop giving energy to?",
];

export default function FutureSelf() {
  const navigate = useNavigate();
  const [outcome] = useState(() => unsaidStore.getOutcome());
  const [xrayOutcome] = useState(() => unsaidStore.getXrayOutcome());
  const [planOutcome] = useState(() => unsaidStore.getPlanOutcome());

  const hash = transcriptHash(outcome?.transcript || []);
  const restored = useRef(unsaidStore.getFutureChat(hash));

  const [phase, setPhase] = useState(() => (restored.current?.phase === "chat" ? "chat" : "intro"));
  const [messages, setMessages] = useState(() => restored.current?.messages || []);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [failed, setFailed] = useState(false);
  const initialClosed = (restored.current?.messages || []).filter((m) => m.role === "user").length >= 2
    && (restored.current?.messages || []).slice(-1)[0]?.role === "future";
  const [closed, setClosed] = useState(initialClosed);
  const scrollRef = useRef(null);
  const startedRef = useRef((restored.current?.messages || []).length > 0);

  const context = {
    name: onboardingState.getAccount()?.name || "",
    handoff: outcome?.handoff || {},
    reveal: outcome?.observations || [],
    xray: xrayOutcome?.xray || {},
    plan: planOutcome?.plan || {},
  };
  const userTurns = messages.filter((m) => m.role === "user").length;

  // Auto-scroll to the newest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking, failed]);

  // Persist the conversation so a reflection survives a page refresh.
  useEffect(() => {
    if (phase === "chat") unsaidStore.setFutureChat(hash, messages, phase);
  }, [messages, phase, hash]);

  // One turn: sends `base` history, appends the future-self reply (or fails gracefully).
  // The 2nd user answer triggers the intentional CLOSING message (short, warm arc).
  const wantsClosing = (msgs) => msgs.filter((m) => m.role === "user").length >= 2;
  const runTurn = async (base) => {
    const closing = wantsClosing(base);
    setFailed(false);
    setThinking(true);
    const { ok, message } = await futureChat(context, base, closing);
    setThinking(false);
    if (!ok) { setFailed(true); return; }
    setMessages([...base, { id: Date.now() + 1, role: "future", text: message }]);
    if (closing) setClosed(true);
  };

  const startChat = async () => {
    if (startedRef.current && messages.length > 0) { setPhase("chat"); return; }
    startedRef.current = true;
    setPhase("chat");
    await runTurn([]);
  };

  const send = async (text) => {
    const content = (text ?? draft).trim();
    if (!content || thinking || closed) return;
    const base = [...messages, { id: Date.now(), role: "user", text: content }];
    setMessages(base);
    setDraft("");
    await runTurn(base);
  };

  const retry = () => runTurn(messages);

  const continueToPurpose = () => {
    unsaidStore.saveFutureOutcome({ hash, messages });
    unsaidStore.appendHistory({ type: "future_self", hash, messages });
    // GA4: Future Self stage completed (step 4).
    trackSessionOnce("step_future", "onboarding_step_completed", { step_name: "future_self", step_number: 4 });
    navigate("/purpose");
  };

  // ---- Intro (perspective-shift transition) ----
  if (phase === "intro") {
    return (
      <OnboardingLayout testId="page-future-self" contentClassName="justify-center py-10">
        <StepProgress current={4} className="mb-10" />
        <EllieBridge feature="future" className="mb-8" />
        <div className="flex flex-col items-center text-center">
          <motion.span initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
            className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-8 w-8" strokeWidth={1.5} />
          </motion.span>
          <Eyebrow className="mt-8">Future Self · One year from today</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight text-foreground">
            There's someone I'd like you to meet.
          </h1>
          <div className="mt-5 space-y-2 text-base leading-relaxed text-muted-foreground">
            <p>Not a stranger.</p>
            <p className="font-medium text-foreground">You — one year from today.</p>
            <p>They've been where you are now. Ask them anything.</p>
          </div>
        </div>
        <div className="mt-12">
          <PrimaryButton data-testid="future-meet" onClick={startChat}>Meet your future self</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Conversation ----
  return (
    <div data-testid="page-future-self" className="fio-immersive flex h-[100dvh] flex-col"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto w-full max-w-[600px] px-5 pt-6 text-center sm:px-8">
        <Eyebrow className="text-primary">Future You · One year from today</Eyebrow>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar">
        <div className="mx-auto flex w-full max-w-[600px] flex-col gap-4 px-5 pb-16 pt-6 sm:px-8">
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div key={m.id} data-testid="future-message" data-role={m.role}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-3xl px-5 py-3.5 text-base leading-relaxed ${m.role === "user" ? "rounded-br-lg bg-primary text-primary-foreground" : "rounded-bl-lg bg-card text-foreground ring-1 ring-border"}`}>
                  {m.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {thinking && (
            <div className="flex justify-start" data-testid="future-thinking">
              <div className="flex items-center gap-1.5 rounded-3xl rounded-bl-lg bg-card px-5 py-4 ring-1 ring-border">
                {[0, 1, 2].map((i) => (<span key={i} className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />))}
              </div>
            </div>
          )}
          {failed && !thinking && (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="future-retry">
              <p className="max-w-[280px] text-sm leading-relaxed text-muted-foreground">
                The line went quiet for a moment. Let's reconnect.
              </p>
              <button data-testid="future-retry-btn" onClick={retry}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-all hover:-translate-y-0.5 hover:border-primary/40">
                <RotateCcw className="h-4 w-4" /> Let me try that again
              </button>
            </div>
          )}
          {!thinking && !failed && messages.length === 1 && !closed && (
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`future-suggestion-${i}`} onClick={() => send(s)}
                  className="rounded-full border border-border bg-card px-4 py-2.5 text-sm text-foreground transition-all hover:-translate-y-0.5 hover:border-primary/40">
                  {s}
                </button>
              ))}
            </div>
          )}
          {closed && !thinking && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 flex flex-col items-center gap-4 pb-2 text-center" data-testid="future-closed">
              <p className="text-sm italic leading-relaxed text-muted-foreground">Your future self will be here when it's time to talk again.</p>
              <button data-testid="future-continue" onClick={continueToPurpose}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground transition-all hover:-translate-y-0.5 hover:bg-primary/90">
                Continue <ArrowRight className="h-5 w-5" />
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {!closed && (
        <div className="border-t border-border bg-background/95 backdrop-blur">
          <div className="mx-auto w-full max-w-[600px] px-5 py-4 sm:px-8">
            <div className="flex items-end gap-2 rounded-3xl bg-card p-2 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
              <textarea data-testid="future-input" rows={1} value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask your future self…"
                className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none" />
              <button data-testid="future-send" onClick={() => send()} disabled={!draft.trim() || thinking}
                aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all enabled:hover:bg-primary/90 disabled:opacity-40">
                <ArrowUp className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
